/**
 * 用户管理（v0.47，**仅管理员**）。
 *
 * ★ 「仅管理员」在这里只体现为**入口不渲染**（App 里按 role 判的）。
 *   真正的拦截在后端：`/api/users` 整棵前缀在权限矩阵里要求 admin。
 *   前端藏按钮不是安全边界 —— 但能把"点了才知道不行"变成"根本点不到"。
 *
 * ⚠️ 界面上**没有**"删除自己 / 停用自己 / 改自己角色"这三个按钮：
 *   后端也会拒（400），这里直接不画 —— 让用户看不到一个注定失败的按钮，
 *   比让他点一次看错误提示更友好。
 */
import { useEffect, useState } from 'react';
import {
  createUser, deleteUser, forceLogoutAll, listUsers, resetUserPassword,
  updateUser,
} from '../api/client';
import { ROLE_CN, ROLE_DESC, USER_ROLES, roleLabel, type Role } from '../lib/auth';
import { useAuthStore, type AuthUser } from '../store/auth';

interface Props {
  onToast: (msg: string, type?: 'info' | 'success' | 'error') => void;
}

const fmt = (s?: string | null) => {
  if (!s) return '—';
  // 后端给的是带 Z 的 ISO（UTC）。`new Date` 会按本地时区渲染，
  // 这里不手工 +8 —— 手工加会与浏览器时区叠加，反而错。
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleString('zh-CN', { hour12: false });
};

export function UserAdminPage({ onToast }: Props) {
  const me = useAuthStore((s) => s.user);
  const [rows, setRows] = useState<AuthUser[]>([]);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [pwFor, setPwFor] = useState<AuthUser | null>(null);
  const [askDel, setAskDel] = useState<AuthUser | null>(null);

  const reload = async () => {
    setBusy(true);
    try {
      setRows(await listUsers());
    } catch (e: any) {
      onToast(`读取账号列表失败：${e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const patch = async (u: AuthUser, body: Parameters<typeof updateUser>[1]) => {
    try {
      const next = await updateUser(u.id, body);
      setRows((rs) => rs.map((r) => (r.id === next.id ? next : r)));
      onToast(`已更新 ${next.username}`, 'success');
    } catch (e: any) {
      onToast(`更新失败：${e.message}`, 'error');
    }
  };

  const act = async (fn: () => Promise<unknown>, okMsg: string) => {
    try {
      await fn();
      onToast(okMsg, 'success');
      await reload();
    } catch (e: any) {
      onToast(`${e.message}`, 'error');
    }
  };

  const isSelf = (u: AuthUser) => me != null && u.id === me.id;

  return (
    <div className="pm-page">
      <div className="pm-head">
        <h2>账号与权限</h2>
        <div className="pm-head-actions">
          <button className="btn" onClick={reload} disabled={busy}>刷新</button>
          <button className="btn primary" onClick={() => setCreating(true)}>新建账号</button>
        </div>
      </div>

      {/* 角色说明摆在这里而不是下拉的 tooltip 里 —— 分配权限的人必须一眼看到
          "检验员和工程师差在哪"，否则四种角色会被用成两种。 */}
      <div className="ua-legend">
        {USER_ROLES.map((r) => (
          <div key={r} className="ua-legend-item">
            <span className={`ua-badge ua-${r}`}>{ROLE_CN[r as Role]}</span>
            <span className="ua-legend-desc">{ROLE_DESC[r as Role]}</span>
          </div>
        ))}
      </div>

      <table className="ua-table">
        <thead>
          <tr>
            <th>用户名</th><th>姓名</th><th>角色</th><th>状态</th>
            <th>最后登录</th><th>操作</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.id} className={u.is_active ? '' : 'ua-off'}>
              <td>
                <b>{u.username}</b>
                {isSelf(u) && <span className="ua-self">（我）</span>}
                {u.created_by && <div className="ua-sub">由 {u.created_by} 创建</div>}
              </td>
              <td>{u.display_name || '—'}</td>
              <td>
                <select className="cell-input" value={u.role} disabled={isSelf(u)}
                        title={isSelf(u) ? '不能修改自己的角色' : ROLE_DESC[u.role as Role]}
                        onChange={(e) => patch(u, { role: e.target.value })}>
                  {USER_ROLES.map((r) => <option key={r} value={r}>{ROLE_CN[r as Role]}</option>)}
                </select>
              </td>
              <td>
                {u.is_active
                  ? <span className="ua-on">启用</span>
                  : <span className="ua-offtag">已停用</span>}
                {u.must_change_password && <div className="ua-sub">待改密码</div>}
              </td>
              <td className="ua-time">{fmt(u.last_login_at)}</td>
              <td className="ua-ops">
                <button className="btn sm" onClick={() => setPwFor(u)}>重置密码</button>
                <button className="btn sm" title="把该账号在所有设备上的登录踢掉"
                        onClick={async () => {
                          try {
                            const r = await forceLogoutAll(u.id);
                            onToast(`已下线 ${r.revoked} 个会话`, 'success');
                          } catch (e: any) {
                            onToast(`操作失败：${e.message}`, 'error');
                          }
                        }}>强制下线</button>
                <button className="btn sm" disabled={isSelf(u)}
                        onClick={() => patch(u, { is_active: !u.is_active })}>
                  {u.is_active ? '停用' : '启用'}
                </button>
                <button className="btn sm btn-danger" disabled={isSelf(u)}
                        onClick={() => setAskDel(u)}>删除</button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && !busy && (
            <tr><td colSpan={6} className="ua-empty">还没有账号</td></tr>
          )}
        </tbody>
      </table>

      {creating && (
        <CreateUserDialog
          onClose={() => setCreating(false)}
          onDone={() => { setCreating(false); reload(); }}
          onToast={onToast}
        />
      )}
      {pwFor && (
        <ResetPasswordDialog
          user={pwFor}
          onClose={() => setPwFor(null)}
          onDone={() => { setPwFor(null); reload(); }}
          onToast={onToast}
        />
      )}
      {askDel && (
        <div className="job-overlay" onClick={() => setAskDel(null)}>
          <div className="job-panel" onClick={(e) => e.stopPropagation()}>
            <h3 className="pwd-title">删除账号</h3>
            <div className="pwd-reason">
              确定删除 <b>{askDel.username}</b>（{roleLabel(askDel.role)}）？
              该账号的所有登录会立即失效。图纸与台账数据**不受影响**。
            </div>
            <div className="pwd-actions">
              <button className="btn" onClick={() => setAskDel(null)}>取消</button>
              <button className="btn btn-danger" onClick={() => {
                const u = askDel;
                setAskDel(null);
                act(() => deleteUser(u.id), `已删除 ${u.username}`);
              }}>确认删除</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ================================================================ 新建账号
function CreateUserDialog({ onClose, onDone, onToast }: {
  onClose: () => void; onDone: () => void;
  onToast: (m: string, t?: 'info' | 'success' | 'error') => void;
}) {
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('viewer');
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!username.trim()) { onToast('请填用户名', 'error'); return; }
    if (pw.length < 8) { onToast('初始密码至少 8 位', 'error'); return; }
    setBusy(true);
    try {
      await createUser({ username: username.trim(), password: pw, role,
                         display_name: name.trim() || null });
      onToast(`已创建 ${username.trim()}（对方首次登录需改密码）`, 'success');
      onDone();
    } catch (e: any) {
      onToast(`创建失败：${e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="job-overlay" onClick={() => !busy && onClose()}>
      <div className="job-panel pwd-panel" onClick={(e) => e.stopPropagation()}>
        <button className="job-close" onClick={onClose}>×</button>
        <h3 className="pwd-title">新建账号</h3>
        <label className="login-field">
          <span>用户名（登录用）</span>
          <input className="login-input" value={username} autoFocus
                 onChange={(e) => setUsername(e.target.value)} />
        </label>
        <label className="login-field">
          <span>姓名 / 备注</span>
          <input className="login-input" value={name}
                 onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="login-field">
          <span>角色</span>
          <select className="cell-input" value={role}
                  onChange={(e) => setRole(e.target.value as Role)}>
            {USER_ROLES.map((r) => <option key={r} value={r}>{ROLE_CN[r as Role]}</option>)}
          </select>
        </label>
        <div className="pwd-reason">{ROLE_DESC[role]}</div>
        <label className="login-field">
          <span>初始密码（至少 8 位）</span>
          <input className="login-input" value={pw}
                 onChange={(e) => setPw(e.target.value)} />
        </label>
        <div className="pwd-reason">
          初始密码只是"第一次进门用"：对方首次登录会被要求自己改掉。
        </div>
        <div className="pwd-actions">
          <button className="btn" onClick={onClose} disabled={busy}>取消</button>
          <button className="btn primary" onClick={submit} disabled={busy}>
            {busy ? '创建中…' : '创建'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ================================================================ 重置密码
function ResetPasswordDialog({ user, onClose, onDone, onToast }: {
  user: AuthUser; onClose: () => void; onDone: () => void;
  onToast: (m: string, t?: 'info' | 'success' | 'error') => void;
}) {
  const me = useAuthStore((s) => s.user);
  const self = me != null && me.id === user.id;
  const [pw, setPw] = useState('');
  const [mustChange, setMustChange] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (pw.length < 8) { onToast('新密码至少 8 位', 'error'); return; }
    setBusy(true);
    try {
      await resetUserPassword(user.id, pw, self ? true : mustChange);
      onToast(`已重置 ${user.username} 的密码（该账号已全部下线）`, 'success');
      onDone();
    } catch (e: any) {
      onToast(`重置失败：${e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="job-overlay" onClick={() => !busy && onClose()}>
      <div className="job-panel pwd-panel" onClick={(e) => e.stopPropagation()}>
        <button className="job-close" onClick={onClose}>×</button>
        <h3 className="pwd-title">重置密码 — {user.username}</h3>
        <label className="login-field">
          <span>新密码（至少 8 位）</span>
          <input className="login-input" value={pw} autoFocus
                 onChange={(e) => setPw(e.target.value)} />
        </label>
        {self ? (
          <div className="pwd-reason">
            你正在重置<b>自己</b>的密码，且**不需要原密码**。因此系统会强制你
            下次登录再改一次 —— 否则这里就成了一个"免密改自己密码"的后门。
          </div>
        ) : (
          <label className="ua-check">
            <input type="checkbox" checked={mustChange}
                   onChange={(e) => setMustChange(e.target.checked)} />
            要求对方下次登录时自己改一次（推荐）
          </label>
        )}
        <div className="pwd-reason">重置后该账号在所有设备上的登录会立即失效。</div>
        <div className="pwd-actions">
          <button className="btn" onClick={onClose} disabled={busy}>取消</button>
          <button className="btn primary" onClick={submit} disabled={busy}>
            {busy ? '提交中…' : '确认重置'}
          </button>
        </div>
      </div>
    </div>
  );
}
